import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ColorSetting,
  EditorGroup,
  PageEditorContextIntro,
  PageEditorShell,
  SegmentedChoiceSetting,
  SelectSetting,
  SliderSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";
import { materializeMegaMenuVisual } from "../megaMenuVisualOwnership";

const STYLE_OPTIONS = [
  { value: "auto", label: "Automático", hint: "Conserva la lógica actual y elige según el catálogo." },
  { value: "elegant", label: "Elegante", hint: "Aire, cards grandes y categorías livianas." },
  { value: "catalog", label: "Catálogo", hint: "Categorías protagonistas y mucha información visible." },
  { value: "showcase", label: "Vidriera", hint: "Prioriza productos destacados con una presencia visual fuerte." },
  { value: "minimal", label: "Minimal", hint: "Más limpio, compacto y con menos ruido visual." },
  { value: "editorial", label: "Editorial", hint: "Intro lateral y productos apilados como una selección curada." },
];

const CONTENT_OPTIONS = [
  { value: "auto", label: "Automático" },
  { value: "products", label: "Productos" },
  { value: "services", label: "Servicios" },
  { value: "mixed", label: "Productos + servicios" },
];

const DISPLAY_OPTIONS = [
  { value: "both", label: "Categorías + ítems" },
  { value: "categories", label: "Sólo categorías" },
  { value: "items", label: "Sólo ítems" },
];

function previewStyle(value) {
  return STYLE_OPTIONS.some((option) => option.value === value) && value !== "auto" ? value : "catalog";
}

function previewColumns(navigation) {
  if (navigation?.megaMenuLayout === "stacked") return 1;
  const configured = Number(navigation?.megaMenuColumns);
  return [2, 3, 4].includes(configured) ? configured : 3;
}

function animationSx(animation) {
  if (animation === "none") return { animation: "none" };
  if (animation === "fade") {
    return {
      animation: "megaMenuEditorFade .22s ease-out",
      "@keyframes megaMenuEditorFade": { from: { opacity: 0 }, to: { opacity: 1 } },
    };
  }
  if (animation === "zoom") {
    return {
      transformOrigin: "top center",
      animation: "megaMenuEditorZoom .22s ease-out",
      "@keyframes megaMenuEditorZoom": {
        from: { opacity: 0, transform: "scale(.98) translateY(-2px)" },
        to: { opacity: 1, transform: "scale(1) translateY(0)" },
      },
    };
  }
  return {
    animation: "megaMenuEditorSlide .2s ease-out",
    "@keyframes megaMenuEditorSlide": {
      from: { opacity: 0, transform: "translateY(-8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  };
}

function StyleThumb({ style, selected }) {
  const bars = style === "catalog" ? 6 : style === "minimal" ? 4 : 3;
  const vertical = style === "editorial";
  return (
    <Box
      sx={{
        height: 70,
        borderRadius: 1.7,
        bgcolor: selected ? "rgba(243,90,58,.07)" : "rgba(25,36,47,.035)",
        border: "1px solid",
        borderColor: selected ? "rgba(243,90,58,.32)" : "rgba(25,36,47,.08)",
        p: 0.8,
        display: "grid",
        gridTemplateColumns: style === "catalog" || style === "editorial" ? "30% 1fr" : "1fr",
        gap: 0.6,
        overflow: "hidden",
      }}
    >
      {(style === "catalog" || style === "editorial") ? <Box sx={{ borderRadius: 1, bgcolor: "rgba(25,36,47,.12)" }} /> : null}
      <Box sx={{ display: "grid", gridTemplateColumns: vertical ? "1fr" : `repeat(${style === "showcase" ? 3 : 2},1fr)`, gap: 0.45, alignContent: "center" }}>
        {Array.from({ length: bars }).slice(0, vertical ? 3 : undefined).map((_, index) => (
          <Box key={index} sx={{ height: vertical ? 14 : style === "minimal" ? 10 : 22, borderRadius: style === "minimal" ? .5 : 1, bgcolor: index === 0 ? "rgba(243,90,58,.28)" : "rgba(25,36,47,.11)" }} />
        ))}
      </Box>
    </Box>
  );
}

function MegaMenuStylePicker({ value, onChange }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 0.9 }}>
      {STYLE_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <Button
            key={option.value}
            onClick={() => onChange(option.value)}
            variant="outlined"
            sx={{
              display: "block",
              p: 0.9,
              minWidth: 0,
              textAlign: "left",
              textTransform: "none",
              borderRadius: 2.2,
              borderWidth: selected ? 2 : 1,
              borderColor: selected ? "#F35A3A" : "rgba(25,36,47,.14)",
              bgcolor: selected ? "rgba(243,90,58,.035)" : "#fff",
              "&:hover": { borderColor: selected ? "#F35A3A" : "rgba(25,36,47,.34)", bgcolor: "rgba(25,36,47,.02)" },
            }}
          >
            <StyleThumb style={option.value === "auto" ? "catalog" : option.value} selected={selected} />
            <Typography sx={{ mt: 0.7, fontWeight: 950, color: "#19242F", fontSize: ".82rem" }}>{option.label}</Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "rgba(25,36,47,.58)", lineHeight: 1.3 }}>{option.hint}</Typography>
          </Button>
        );
      })}
    </Box>
  );
}

function PreviewProductCard({ visual, stacked = false, index = 0 }) {
  return (
    <Box sx={{ display: stacked ? "flex" : "block", minWidth: 0, overflow: "hidden", borderRadius: 1.7, border: "1px solid", borderColor: visual.previewCard.borderColor, bgcolor: visual.previewCard.backgroundColor, boxShadow: visual.previewCard.boxShadow }}>
      <Box sx={{ width: stacked ? 82 : "100%", minWidth: stacked ? 82 : 0, height: stacked ? 72 : 58, bgcolor: visual.previewCard.imageBackgroundColor }} />
      <Box sx={{ minWidth: 0, flex: 1, p: 0.8 }}>
        <Typography variant="caption" sx={{ display: "block", color: visual.previewCard.categoryTextColor, fontSize: ".62rem" }}>Categoría</Typography>
        <Typography noWrap sx={{ color: visual.previewCard.titleTextColor, fontSize: ".76rem", fontWeight: 900 }}>Producto {index + 1}</Typography>
        <Typography noWrap variant="caption" sx={{ color: visual.previewCard.descriptionTextColor, fontSize: ".61rem" }}>Descripción de muestra</Typography>
      </Box>
    </Box>
  );
}

function PreviewCategories({ navigation, visual }) {
  const count = navigation?.megaMenuLayout === "stacked" ? 4 : previewColumns(navigation) * 2;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${previewColumns(navigation)},minmax(0,1fr))`, gap: 0.55 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ px: 0.75, py: 0.6, minWidth: 0, borderRadius: 1.4, border: "1px solid", borderColor: visual.categoryRow.borderColor, bgcolor: visual.categoryRow.backgroundColor }}>
          <Typography noWrap sx={{ color: visual.categoryRow.labelColor, fontWeight: 850, fontSize: ".72rem" }}>Categoría {index + 1}</Typography>
          {navigation?.showMegaMenuStats === true ? <Typography variant="caption" sx={{ color: visual.categoryRow.countColor, fontSize: ".6rem" }}>6 productos</Typography> : null}
        </Box>
      ))}
    </Box>
  );
}

function MegaMenuPreview({ form, device }) {
  const navigation = form?.navigation || {};
  const visual = materializeMegaMenuVisual(navigation, form?.theme, form?.theme?.designTokens).megaMenuVisual;
  const style = previewStyle(navigation.megaMenuStyle);
  const borderWidth = Math.max(0, Math.min(4, Number(visual.surface.borderWidth ?? 1) || 0));
  const radius = Math.max(0, Math.min(64, Number(visual.surface.borderRadius ?? 22) || 0));
  const stacked = navigation.megaMenuLayout === "stacked";
  const showCategories = navigation.megaMenuDisplay !== "items";
  const showItems = navigation.megaMenuDisplay !== "categories";
  const showIntro = navigation.showMegaMenuIntro === true;
  const replayKey = [style, navigation.megaMenuLayout, navigation.megaMenuColumns, navigation.megaMenuAnimation, borderWidth, radius].join(":");

  const intro = showIntro ? (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" sx={{ color: style === "catalog" ? visual.eyebrow.introColor : visual.eyebrow.color, fontWeight: 950, fontSize: ".62rem", lineHeight: 1 }}>{navigation.megaMenuEyebrow || "COLECCIÓN"}</Typography>
      <Typography sx={{ mt: .35, color: style === "catalog" ? visual.title.introColor : visual.title.color, fontWeight: 950, fontSize: ".98rem", lineHeight: 1.05 }}>{navigation.megaMenuTitle || "Explorá el catálogo"}</Typography>
      <Typography sx={{ mt: .45, color: style === "catalog" ? visual.description.introColor : visual.description.color, fontSize: ".67rem", lineHeight: 1.4 }}>{navigation.megaMenuDescription || "Un acceso rápido a lo más importante de tu catálogo."}</Typography>
      {navigation.showMegaMenuStats === true ? <Stack direction="row" spacing={.4} sx={{ mt: .8 }}><Chip size="small" label="18 ítems" sx={{ height: 20, fontSize: ".6rem" }} /><Chip size="small" label="6 categorías" sx={{ height: 20, fontSize: ".6rem" }} /></Stack> : null}
    </Box>
  ) : null;

  let content = null;
  if (style === "catalog") {
    content = (
      <Box sx={{ display: "grid", gridTemplateColumns: showIntro ? "28% 1fr" : "1fr" }}>
        {showIntro ? <Box sx={{ p: 1.5, bgcolor: visual.introSurface.backgroundColor, borderColor: visual.introSurface.borderColor }}>{intro}</Box> : null}
        <Stack spacing={1} sx={{ p: 1.5, minWidth: 0 }}>
          <Typography sx={{ color: visual.categoriesTitle.color, fontWeight: 950, fontSize: ".9rem" }}>{navigation.productsLabel || "Productos"}</Typography>
          {showCategories ? <PreviewCategories navigation={navigation} visual={visual} /> : null}
          {showItems ? <Stack direction="row" spacing={.5}>{[0, 1, 2].map((index) => <Chip key={index} size="small" label={`Nuevo ${index + 1}`} sx={{ maxWidth: 110, height: 22, color: visual.latestItem.textColor, bgcolor: visual.latestItem.backgroundColor, border: "1px solid", borderColor: visual.latestItem.borderColor, fontSize: ".61rem" }} />)}</Stack> : null}
        </Stack>
      </Box>
    );
  } else if (style === "editorial") {
    content = (
      <Box sx={{ display: "grid", gridTemplateColumns: showIntro ? "34% 1fr" : "1fr", gap: 1.3, p: 1.6 }}>
        {showIntro ? <Box sx={{ pr: 1.2, borderRight: "1px solid", borderColor: visual.divider.color }}>{intro}</Box> : null}
        <Stack spacing={.65} sx={{ minWidth: 0 }}>
          {showItems ? [0, 1, 2].map((index) => <PreviewProductCard key={index} visual={visual} stacked index={index} />) : null}
          {showCategories ? <Stack direction="row" flexWrap="wrap" useFlexGap gap={.45}>{["Hogar", "Exterior", "Ofertas"].map((label) => <Chip key={label} label={label} size="small" sx={{ height: 22, color: visual.categoryPill.textColor, bgcolor: visual.categoryPill.backgroundColor, fontSize: ".61rem" }} />)}</Stack> : null}
        </Stack>
      </Box>
    );
  } else if (style === "minimal") {
    content = (
      <Stack spacing={1} sx={{ p: 1.5 }}>
        {intro}
        {showCategories ? <PreviewCategories navigation={navigation} visual={visual} /> : null}
        {showItems ? <Box sx={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : "repeat(2,minmax(0,1fr))", gap: .65 }}>{[0, 1].map((index) => <PreviewProductCard key={index} visual={visual} stacked index={index} />)}</Box> : null}
      </Stack>
    );
  } else {
    const cardCount = style === "showcase" ? 3 : 2;
    content = (
      <Stack spacing={1.1} sx={{ p: 1.65 }}>
        {intro}
        {showItems ? <Box sx={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : `repeat(${cardCount},minmax(0,1fr))`, gap: .7 }}>{Array.from({ length: cardCount }).map((_, index) => <PreviewProductCard key={index} visual={visual} stacked={stacked} index={index} />)}</Box> : null}
        {showCategories ? <Stack direction="row" flexWrap="wrap" useFlexGap gap={.45}>{["Categoría", "Colección", "Temporada"].map((label) => <Chip key={label} label={label} size="small" sx={{ height: 22, color: visual.categoryPill.textColor, bgcolor: visual.categoryPill.backgroundColor, border: "1px solid", borderColor: visual.categoryPill.borderColor, fontSize: ".61rem" }} />)}</Stack> : null}
      </Stack>
    );
  }

  return (
    <Box sx={{ minHeight: device === "mobile" ? 440 : 520, bgcolor: "#F1F3F6", p: device === "mobile" ? 1 : 2 }}>
      <Paper elevation={0} sx={{ px: 1.4, py: 1, borderRadius: 0, bgcolor: "#fff", borderBottom: "1px solid rgba(25,36,47,.09)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontWeight: 950, color: "#19242F", fontSize: ".82rem" }}>{form?.business?.name || "Tu marca"}</Typography>
          <Stack direction="row" spacing={1.1}><Typography variant="caption">Inicio</Typography><Typography variant="caption" sx={{ fontWeight: 900 }}>{navigation.productsLabel || "Productos"} ▾</Typography><Typography variant="caption">Contacto</Typography></Stack>
        </Stack>
      </Paper>
      {device === "mobile" ? <Typography variant="caption" sx={{ display: "block", py: .8, color: "rgba(25,36,47,.58)", fontWeight: 800 }}>El megamenú real se abre en escritorio; esta vista mantiene el preview para poder editarlo.</Typography> : null}
      <Box sx={{ px: device === "mobile" ? 0 : 1.2 }}>
        <Box
          key={replayKey}
          sx={{
            maxWidth: style === "minimal" ? 900 : style === "elegant" ? 980 : 1080,
            mx: "auto",
            overflow: "hidden",
            bgcolor: visual.surface.backgroundColor,
            border: borderWidth > 0 ? `${borderWidth}px solid` : "none",
            borderColor: visual.surface.borderColor,
            borderRadius: `0 0 ${radius}px ${radius}px`,
            boxShadow: visual.surface.boxShadow,
            ...animationSx(navigation.megaMenuAnimation || "slide"),
          }}
        >
          {content}
        </Box>
      </Box>
    </Box>
  );
}

export default function MegaMenuEditor({ form, device, saving = false, onCommit }) {
  const navigation = form?.navigation || {};
  const visual = materializeMegaMenuVisual(navigation, form?.theme, form?.theme?.designTokens).megaMenuVisual;
  const set = React.useCallback((key, value) => onCommit?.(`navigation.${key}`, value), [onCommit]);
  const setVisual = React.useCallback((key, value) => onCommit?.(`navigation.megaMenuVisual.${key}`, value), [onCommit]);
  const shadowEnabled = String(visual.surface.boxShadow || "none").trim().toLowerCase() !== "none";

  return (
    <PageEditorShell
      title="Editor de megamenú"
      description="Diseño y comportamiento del menú desplegable de escritorio. No modifica el navbar ni las páginas."
      surfaceLabel="Megamenú"
      device={device}
      saving={saving}
      preview={<MegaMenuPreview form={form} device={device} />}
    >
      <PageEditorContextIntro>
        Esta superficie edita únicamente el megamenú. El modo Automático conserva la lógica que ya tenían las webs publicadas.
      </PageEditorContextIntro>

      <EditorGroup title="Estilo" description="Cinco variantes reales más el modo automático compatible con lo anterior.">
        <MegaMenuStylePicker value={navigation.megaMenuStyle || "auto"} onChange={(value) => set("megaMenuStyle", value)} />
      </EditorGroup>

      <EditorGroup title="Distribución y movimiento" description="Controlá cómo se ordena el contenido y cómo aparece el desplegable.">
        <SegmentedChoiceSetting
          label="Apilado"
          value={navigation.megaMenuLayout || "grid"}
          onChange={(value) => set("megaMenuLayout", value)}
          options={[{ value: "grid", label: "Grilla" }, { value: "stacked", label: "Apilado" }]}
        />
        <SelectSetting
          label="Columnas"
          value={navigation.megaMenuColumns || "auto"}
          onChange={(value) => set("megaMenuColumns", value)}
          options={[{ value: "auto", label: "Automáticas" }, { value: "2", label: "2 columnas" }, { value: "3", label: "3 columnas" }, { value: "4", label: "4 columnas" }]}
          helperText={navigation.megaMenuLayout === "stacked" ? "En modo apilado se usa una sola columna." : "Automáticas mantiene el comportamiento responsive anterior."}
        />
        <SegmentedChoiceSetting
          label="Animación de apertura"
          value={navigation.megaMenuAnimation || "slide"}
          onChange={(value) => set("megaMenuAnimation", value)}
          options={[{ value: "slide", label: "Deslizar" }, { value: "fade", label: "Fundido" }, { value: "zoom", label: "Zoom" }, { value: "none", label: "Ninguna" }]}
        />
      </EditorGroup>

      <EditorGroup title="Superficie" description="Podés dejarlo totalmente plano o darle forma y profundidad.">
        <SliderSetting label="Grosor del borde" value={Number(visual.surface.borderWidth ?? 1)} onChange={(value) => setVisual("surface.borderWidth", value)} min={0} max={4} step={1} unit="px" />
        <SliderSetting label="Redondeado" value={Number(visual.surface.borderRadius ?? 22)} onChange={(value) => setVisual("surface.borderRadius", value)} min={0} max={64} step={1} unit="px" />
        <ToggleSetting
          label="Sombra"
          checked={shadowEnabled}
          onChange={(checked) => setVisual("surface.boxShadow", checked ? "0 24px 58px rgba(18,25,38,.14)" : "none")}
          helperText="Desactivala para un megamenú completamente plano."
        />
        <ColorSetting label="Fondo" value={visual.surface.backgroundColor} fallback="#FFFFFF" onChange={(value) => setVisual("surface.backgroundColor", value)} />
        <ColorSetting label="Color del borde" value={visual.surface.borderColor} fallback="rgba(25,36,47,.12)" onChange={(value) => setVisual("surface.borderColor", value)} />
        <ColorSetting label="Fondo del bloque destacado" value={visual.introSurface.backgroundColor} fallback="#19242F" onChange={(value) => setVisual("introSurface.backgroundColor", value)} />
      </EditorGroup>

      <EditorGroup title="Contenido" description="Elegí qué llena el megamenú sin cambiar los productos ni las categorías reales.">
        <SelectSetting label="Fuente" value={navigation.megaMenuContent || "auto"} onChange={(value) => set("megaMenuContent", value)} options={CONTENT_OPTIONS} />
        <SelectSetting label="Mostrar" value={navigation.megaMenuDisplay || "both"} onChange={(value) => set("megaMenuDisplay", value)} options={DISPLAY_OPTIONS} />
        <SelectSetting
          label="Jerarquía"
          value={navigation.megaMenuHierarchy || "category-first"}
          onChange={(value) => set("megaMenuHierarchy", value)}
          options={[{ value: "category-first", label: "Categoría primero" }, { value: "subcategory-first", label: "Subcategoría primero" }]}
        />
        <ToggleSetting label="Mostrar introducción" checked={navigation.showMegaMenuIntro === true} onChange={(value) => set("showMegaMenuIntro", value)} />
        <ToggleSetting label="Mostrar cantidades" checked={navigation.showMegaMenuStats === true} onChange={(value) => set("showMegaMenuStats", value)} />
        <ToggleSetting label="Mostrar últimos agregados" checked={navigation.showMegaMenuLatest === true} onChange={(value) => set("showMegaMenuLatest", value)} />
      </EditorGroup>

      {navigation.showMegaMenuIntro === true ? (
        <EditorGroup title="Texto destacado" description="Copy opcional del bloque introductorio del megamenú.">
          <TextSetting label="Texto superior" value={navigation.megaMenuEyebrow} onChange={(value) => set("megaMenuEyebrow", value)} />
          <TextSetting label="Título" value={navigation.megaMenuTitle} onChange={(value) => set("megaMenuTitle", value)} />
          <TextSetting label="Descripción" value={navigation.megaMenuDescription} onChange={(value) => set("megaMenuDescription", value)} multiline minRows={3} />
        </EditorGroup>
      ) : null}
    </PageEditorShell>
  );
}
